import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { CdkDragDrop, moveItemInArray, CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { Bind } from 'primeng/bind';
import { SelectButton } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { NgClass } from '@angular/common';
import { Skeleton } from 'primeng/skeleton';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-thumbnail-viewer',
  templateUrl: './thumbnail-viewer.component.html',
  styleUrls: ['./thumbnail-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Bind,
    SelectButton,
    FormsModule,
    PrimeTemplate,
    CdkDropList,
    NgClass,
    Skeleton,
    CdkDrag,
    TranslatePipe
  ]
})
export class ThumbnailViewerComponent {
  /**
   * Sets the thumbnail images and initializes page order tracking.
   * Creates a mapping array to track original page positions after reordering.
   * @param value - Array of thumbnail image URLs or base64 strings
   */
  @Input()
  set thumbnails(value: string[]) {
    this._thumbnails = value;
    this.originalPageOrder = Array.from({ length: value.length }, (_, i) => i);
  }
  @Input() fileName: string = '';
  @Input() selectedPages: number[] = [];
  @Input() mode: 'view' | 'split' = 'view';
  @Input() isLoading = false;
  @Output() pageClicked = new EventEmitter<number>();
  @Output() thumbnailsReordered = new EventEmitter<string[]>();
  @Output() selectedPagesChange = new EventEmitter<number[]>();
  @Output() selectedDocumentPages = new EventEmitter<number[]>();
  Array = Array;

  private originalPageOrder: number[] = [];
  private _thumbnails: string[] = [];
  thumbnailSize: 'small' | 'medium' | 'large' = 'medium';
  focusedIndex = 0;
  screenReaderMessage = '';

  thumbnailOptions = [
    {
      label: 'documentSplit.small',
      value: 'small',
      icon: 'fa-solid fa-grid-4'
    },
    {
      label: 'documentSplit.medium',
      value: 'medium',
      icon: 'fa-solid fa-grid'
    },
    {
      label: 'documentSplit.large',
      value: 'large',
      icon: 'fa-solid fa-grid-2'
    }
  ];

  /**
   * Gets the current thumbnail images array.
   */
  get thumbnails(): string[] {
    return this._thumbnails;
  }

  /**
   * Maps current selection indices to their corresponding original page positions.
   */
  private updateSelectedDocumentPages() {
    const documentPageNumbers = this.selectedPages.map(index => this.originalPageOrder[index]);
    this.selectedDocumentPages.emit(documentPageNumbers);
  }

  @ViewChild('thumbnailGrid', { static: false }) thumbnailGrid!: ElementRef;

  /**
   * Handles keyboard navigation and reordering within the thumbnail grid.
   * Supports arrow keys for navigation, Ctrl+arrows for reordering
   * @param event - The keyboard event
   * @param index - The index of the currently focused thumbnail
   */
  onKeyDown(event: KeyboardEvent, index: number) {
    const buttons = this.thumbnailGrid.nativeElement.querySelectorAll(
      'button.thumbnail'
    ) as NodeListOf<HTMLElement>;
    const gridColumns = this.getGridColumns();
    const ctrl = event.ctrlKey || event.metaKey;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (ctrl) {
          this.reorder(index, Math.min(index + gridColumns, this.thumbnails.length - 1));
        } else {
          this.moveFocus(Math.min(index + gridColumns, this.thumbnails.length - 1), buttons);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (ctrl) {
          this.reorder(index, Math.max(index - gridColumns, 0));
        } else {
          this.moveFocus(Math.max(index - gridColumns, 0), buttons);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (ctrl) {
          this.reorder(index, Math.min(index + 1, this.thumbnails.length - 1));
        } else {
          this.moveFocus(Math.min(index + 1, this.thumbnails.length - 1), buttons);
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (ctrl) {
          this.reorder(index, Math.max(index - 1, 0));
        } else {
          this.moveFocus(Math.max(index - 1, 0), buttons);
        }
        break;
      case 'Home':
        event.preventDefault();
        this.moveFocus(0, buttons);
        break;
      case 'End':
        event.preventDefault();
        this.moveFocus(this.thumbnails.length - 1, buttons);
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        this.onClickPage(index);
        break;
    }
  }

  /**
   * Moves focus to a specific thumbnail in the grid.
   * @param newIndex - The index of the thumbnail to focus
   * @param buttons - NodeList of thumbnail button elements
   */
  private moveFocus(newIndex: number, buttons: NodeListOf<HTMLElement>) {
    this.focusedIndex = newIndex;
    buttons[newIndex]?.focus();
  }

  /**
   * Reorders thumbnails and updates selected page indices accordingly.
   * Handles both keyboard and drag-and-drop reordering operations.
   * @param previousIndex - The original index of the thumbnail being moved
   * @param currentIndex - The new index where the thumbnail should be placed
   */
  private reorder(previousIndex: number, currentIndex: number) {
    if (previousIndex === currentIndex) return;

    moveItemInArray(this.thumbnails, previousIndex, currentIndex);
    moveItemInArray(this.originalPageOrder, previousIndex, currentIndex);

    const newSelectedPages = this.selectedPages.map(selectedIndex => {
      if (selectedIndex === previousIndex) {
        return currentIndex;
      } else if (
        previousIndex < currentIndex &&
        selectedIndex > previousIndex &&
        selectedIndex <= currentIndex
      ) {
        return selectedIndex - 1;
      } else if (
        previousIndex > currentIndex &&
        selectedIndex >= currentIndex &&
        selectedIndex < previousIndex
      ) {
        return selectedIndex + 1;
      }
      return selectedIndex;
    });

    this.thumbnailsReordered.emit(this.thumbnails);
    this.selectedPagesChange.emit(newSelectedPages);

    const documentPageNumbers = newSelectedPages.map(index => this.originalPageOrder[index]);
    this.selectedDocumentPages.emit(documentPageNumbers);

    this.focusedIndex = currentIndex;
    const buttons = this.thumbnailGrid.nativeElement.querySelectorAll(
      'button.thumbnail'
    ) as NodeListOf<HTMLElement>;
    buttons[currentIndex]?.focus();

    this.screenReaderMessage = `Page ${previousIndex + 1} moved to position ${currentIndex + 1}`;
  }

  /**
   * Handles drag and drop reordering operations.
   * @param event - The CDK drag-drop event containing previous and current indices
   */
  drop(event: CdkDragDrop<string[]>) {
    const { previousIndex, currentIndex } = event;
    this.reorder(previousIndex, currentIndex);
  }

  /**
   * Checks if a page at the given index is currently selected.
   * @param index - The page index to check
   * @returns True if the page is selected, false otherwise
   */
  isSelected(index: number): boolean {
    return this.selectedPages.includes(index);
  }

  /**
   * Handles page thumbnail click events.
   * @param index - The index of the clicked page
   */
  onClickPage(index: number) {
    if (this.mode === 'split') {
      this.pageClicked.emit(index);
      setTimeout(() => this.updateSelectedDocumentPages(), 0);
    }
  }

  /**
   * Sets the display size for thumbnails.
   * @param size - The desired thumbnail size ('small', 'medium', or 'large')
   */
  setSize(size: 'small' | 'medium' | 'large') {
    this.thumbnailSize = size;
  }

  /**
   * Gets the number of columns in the grid based on current thumbnail size.
   * @returns The number of columns in the current grid layout
   */
  private getGridColumns(): number {
    if (!this.thumbnailGrid?.nativeElement) return 4;

    const containerWidth = this.thumbnailGrid.nativeElement.offsetWidth;
    const thumbnailWidth = this.getThumbnailWidth();
    const gap = 24; // From CSS

    return Math.floor((containerWidth + gap) / (thumbnailWidth + gap)) || 1;
  }

  /**
   * Gets the pixel width for the current thumbnail size setting.
   */
  private getThumbnailWidth(): number {
    switch (this.thumbnailSize) {
      case 'small':
        return 125;
      case 'medium':
        return 200;
      case 'large':
        return 250;
      default:
        return 200;
    }
  }

  /**
   * Determines the tab index for a thumbnail based on focus state.
   * @param index - The index of the thumbnail
   * @returns 0 if focused, -1 otherwise
   */
  getTabIndex(index: number): number {
    return index === this.focusedIndex ? 0 : -1;
  }
}
